import React, { useEffect, useState } from "react";

import { Platform, StyleSheet, View } from "react-native";
import {
  Map,
  Text,
  Icon,
  Toast,
  Button,
  TextArea,
  EditModal,
  CityPicker,
  InputLabel,
  MapViewBox,
  GroupInfoBox,
  ConfirmAlert,
  AddItemButton,
  CountryPicker,
  ProvincePicker,
  DeleteAlertComponent,
} from "components";

import { useFlag } from "hooks";
import { changeArrayAt, sn } from "utils";
import { colors, strings } from "assets/constants";
import css from "styles";

export default function Addresses(props: {
  addresses: AddressT[];
  onChange: (addresses: AddressT[]) => void;
  disabled?: boolean;
}) {
  const { addresses, onChange, disabled } = props;

  const [itemsState, setItemsState] = useState<Array<AddressT>>(addresses);
  useEffect(() => {
    setItemsState(addresses);
  }, [addresses]);

  const [indexToEdit, setIndexToEdit] = useState<number | null>(null);
  const { flg: modal, on: showModal, off: hideModal } = useFlag();
  const { flg: alert, on: showAlert, off: hideAlert } = useFlag();
  const { flg: mapView, on: showMap, off: hideMap } = useFlag();

  const [fields, setFields] = useState<AddressT>();
  const updateFields = (newVal: AddressT) =>
    setFields((prev) => ({ ...prev, ...newVal }));

  const _onChangeCountry = (input?: PickerItemT) => {
    const country = input as LocationT;
    updateFields({ country, province: undefined, city: undefined });
  };

  const _onChangeProvince = (input?: PickerItemT) => {
    const province = input as LocationT;
    updateFields({ province, city: undefined });
  };

  const _onChangeCity = (input?: PickerItemT) => {
    const city = input as LocationT;
    updateFields({ city });
  };

  const _onChangeAddress = (address: string) => updateFields({ address });
  const _onChangeLocation = (location: { lat: string; lng: string }) =>
    updateFields({ location });

  const resetForm = () => {
    setIndexToEdit(null);
    setFields({});
  };

  const selectProvinceWarn = () =>
    Toast.warning({ message: strings.please_pick_country });

  const selectCityWarn = () =>
    Toast.warning({ message: strings.please_pick_province });

  const _onAdd = () => {
    fields && onChange(addresses ? [...addresses, fields] : [fields]);
    resetForm();
  };

  const _onEdit = () => {
    const tmp =
      typeof indexToEdit === "number" &&
      changeArrayAt(itemsState, indexToEdit, fields);
    tmp && onChange(tmp);
  };

  const _onDelete = (close: () => void) => {
    const tmp =
      typeof indexToEdit === "number" && changeArrayAt(itemsState, indexToEdit);
    tmp && onChange(tmp);
    resetForm();
    close();
  };

  const _onConfirm = () => {
    if (
      !fields?.country ||
      !fields.province
      // !fields.city ||
      // !fields.address
    ) {
      Toast.error({ message: strings.fill_all_fields });
      return;
    }
    indexToEdit === null ? _onAdd() : _onEdit();
    resetForm();
    hideModal();
  };

  const _onCancel = () => {
    resetForm();
    hideModal();
  };

  return (
    <>
      <View style={css.responsiveListContainer}>
        {itemsState?.length > 0 ? (
          itemsState?.map((address: AddressT, key: number) => {
            return (
              <View key={String(key)} style={css.responsiveBoxContainer}>
                <GroupInfoBox
                  key={String(key)}
                  onEdit={() => {
                    setIndexToEdit(key);
                    setFields(address);
                    showModal();
                  }}
                  onDelete={() => {
                    setIndexToEdit(key);
                    showAlert();
                  }}
                  title={
                    address?.province?.text +
                    (address?.city?.text ? " - " + address?.city?.text : "")
                  }
                  icon={{ name: "map-pin" }}
                  renderContent={() => (
                    <Text style={{ lineHeight: 22 }}>{address?.address}</Text>
                  )}
                />
              </View>
            );
          })
        ) : (
          <View style={{ marginBottom: 10 }}>
            <Text size="sm" color={colors.textColorGray} style={{}}>
              {strings.no_customer_address}
            </Text>
          </View>
        )}
      </View>
      {!disabled && (
        <AddItemButton onPress={showModal} title={strings.add_new_address} />
      )}
      <EditModal
        visible={modal}
        onCancel={_onCancel}
        onConfirm={_onConfirm}
        renderForm={() => {
          return (
            <>
              <InputLabel label={strings.country} />
              <CountryPicker
                id={fields?.country?.id}
                value={fields?.country}
                onSelect={_onChangeCountry}
              />

              <InputLabel label={strings.province} />
              <ProvincePicker
                onPress={fields?.country ? undefined : selectProvinceWarn}
                id={fields?.country?.id}
                value={fields?.province}
                onSelect={_onChangeProvince}
              />

              <InputLabel label={strings.city} />
              <CityPicker
                onPress={fields?.province ? undefined : selectCityWarn}
                id={fields?.province?.id}
                value={fields?.city}
                onSelect={_onChangeCity}
              />

              {Platform.OS !== "web" && (
                <>
                  {fields?.location ? (
                    <View style={styles.mapViewBox}>
                      <MapViewBox
                        coordinate={fields.location}
                        onPress={showMap}
                      />
                    </View>
                  ) : (
                    <Button
                      onPress={showMap}
                      style={{
                        marginBottom: 7,
                        marginTop: 4,
                        ...css.selfCenter,
                      }}
                      title={{ value: strings.location }}
                      icon={{ name: "map-pin" }}
                      bgColor={colors.blue}
                      width={200}
                    />
                  )}
                </>
              )}

              <InputLabel label={strings.address} />
              <TextArea
                value={fields?.address}
                onChangeText={_onChangeAddress}
                placeholder={strings.insert_address}
                icon={{ name: "map" }}
              />
            </>
          );
        }}
      />

      <Map
        visible={mapView}
        hide={hideMap}
        initialCoordinate={fields?.location}
        onConfirmLocation={_onChangeLocation}
      />

      <ConfirmAlert
        force
        show={alert}
        hide={hideAlert}
        renderContent={(close) => (
          <DeleteAlertComponent
            removeLoading={false}
            remove={() => _onDelete(close)}
            cancel={close}
          />
        )}
      />
    </>
  );
}

const styles = StyleSheet.create({
  mapViewBox: {
    width: "100%",
    height: 150,
    backgroundColor: colors.white,
    marginBottom: 20,
  },
});
